import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useTheme,
  Checkbox,
  Dialog,
  DialogContent,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
  Button,
  Alert,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import DialogTitle from "../DialogTitle/DialogTitle";
import { Props, State } from "./interfaces";
import { Block, DocumentViewerContainer, Field, Container, Label } from "./styles";
import { DefaultExtensionType, FileIcon } from "react-file-icon";
import { FileIconButton } from "../AttachedFiles/styles";
import AutocompleteDropdown from "../../reusable-components/AutocompleteDropdown/AutocompleteDropdown";
import { OutgoingMailPostingOptionApiService } from "../../services/outgoing-mail-posting-option/outgoing-mail-posting-option-api.service";
import { OutgoingMailDTO } from "../../models/outgoing-mail";
import { MailInboxAddresseesApiService } from "../../services/mail-inbox-addressees/mail-inbox-addressees-api.service";
import DialogActions from "../DialogActions/DialogActions";
import { OutgoingMailApiService } from "../../services/outgoing-mail/outgoing-mail-api.service";
import ConfirmationChangesDialog from "../ConfirmationChangesDialog/ConfirmationChangesDialog";
import DocumentViewer from "src/reusable-components/DocumentViewer/DocumentViewer";
import { getFileExtension } from "src/utils/files-utils/get-file-extension";
import { customFileIconStyles } from "src/reusable-components/DefaultFileIconStyles/default-file-icon-styles";
import { OUTGOING_MAIL_STATUSES } from "src/utils/constants/constants";
import { CountriesApiService } from "src/services/countries/countries-api.service";
import {
  MailInboxAddresseeDTO,
  MailInboxAddresseeFilter,
  MailInboxAddresseeRef,
} from "src/models/mail-inbox-addressee";
import AddEditMailInboxAddressee from "../AddEditMailInboxAddressee/AddEditMailInboxAddressee";
import CustomFetchingDropdown from "../../reusable-components/CustomFetchingDropdown/CustomFetchingDropdown";
import { EMAIL_PATTERN, isValidString, PHONE_NUMBER_PATTERN } from "src/utils/validation/isValidString";
import CustomConfirmationChangesDialog from "../CustomConfirmationChangesDialog/CustomConfirmationChangesDialog";
import SimplePopover from "src/reusable-components/SimplePopover/SimplePopover";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLightbulb } from "@fortawesome/free-solid-svg-icons";
import SuggestedByAiHint from "../../views/manage-mail/SuggestedByAiHint";
import LoadingOverlay from "../LoadingOverlay/LoadingOverlay";

const EditOutgoingMail = ({ item, handleNotifyParent, readOnly }: Props) => {
  const tRP = "components.edit_outgoing_mail";
  const tRPFields = "components.edit_outgoing_mail.fields";

  const { t } = useTranslation();
  const theme = useTheme();

  const INITIAL_STATE: State = useMemo(
    () => ({
      item: {
        ...item,
        contactPersonName: item.contactPersonName ?? item.contactPersonNameOcr,
        companyName: item.companyName ?? item.companyNameOcr,
        addressedTo: item.addressedTo ?? item.addressedToOcr,
        phoneNumber: item.phoneNumber ?? item.phoneNumberOcr,
        email: item.email ?? item.emailOcr,
        addressLineOne: item.addressLineOne ?? item.addressLineOneOcr,
        addressLineTwo: item.addressLineTwo ?? item.addressLineTwoOcr,
        zip: item.zip ?? item.zipOcr,
        location: item.location ?? item.locationOcr,
        country: item.country ?? item.countryOcr,
      },
      open: false,
      loading: false,
      CCDOpen: false,
      CCCDOpen: false,
      error: "",
      fileToView: null,
      newAddressee: null,
      fieldsMutated: [],
    }),
    [item]
  );

  const noChangesAllowed =
    readOnly ||
    item.status?.id === OUTGOING_MAIL_STATUSES.Posted.id ||
    item.status?.id === OUTGOING_MAIL_STATUSES.Discarded.id;

  const [state, setState] = useState<State>({
    ...INITIAL_STATE,
  });

  //#region handle change
  const handleChange = useCallback((field: keyof OutgoingMailDTO, value: OutgoingMailDTO[keyof OutgoingMailDTO]) => {
    setState((state) => ({
      ...state,
      item: {
        ...state.item,
        [field]: value,
      },
      fieldsMutated: [...state.fieldsMutated.filter((_field) => _field !== field)],
    }));
  }, []);
  //#endregion

  //#region change addressee
  const handleChangeAdressedTo = useCallback((override: boolean) => {
    setState((state) => ({
      ...state,
      CCCDOpen: false,
      item: {
        ...state.item,
        addressedTo: state.newAddressee,
        ...(override && {
          contactPersonName: state.newAddressee?.contactPersonName ?? "",
          companyName: state.newAddressee?.companyName ?? "",
          phoneNumber: state.newAddressee?.phoneNumber ?? "",
          email: state.newAddressee?.email ?? "",
          addressLineOne: state.newAddressee?.addressLineOne ?? "",
          addressLineTwo: state.newAddressee?.addressLineTwo ?? "",
          zip: state.newAddressee?.zip ?? "",
          location: state.newAddressee?.location ?? "",
          country: state.newAddressee?.country ?? null,
        }),
      },
      newAddressee: null,
    }));
  }, []);

  const checkIfDataAlreadyFilled = useCallback(() => {
    return (
      !!state.item.addressLineOne ||
      !!state.item.addressLineTwo ||
      !!state.item.companyName ||
      !!state.item.contactPersonName ||
      !!state.item.country ||
      !!state.item.email ||
      !!state.item.phoneNumber ||
      !!state.item.location ||
      !!state.item.zip
    );
  }, [state.item]);

  useEffect(() => {
    const alreadyFilledData = checkIfDataAlreadyFilled();

    if (state.newAddressee && state.newAddressee.id !== state.item.addressedTo?.id && alreadyFilledData) {
      setState((state) => ({
        ...state,
        CCCDOpen: true,
      }));
    } else if (state.newAddressee && !alreadyFilledData) {
      handleChangeAdressedTo(true);
    }
  }, [state.newAddressee]);
  //#endregion

  //#region save
  const handleSave = useCallback(async () => {
    setState((state) => ({ ...state, loading: true }));
    try {
      const response = await new OutgoingMailApiService().saveItems([
        {
          ...state.item,
          doNotProcessByOcr: true,
        },
      ]);
      if (response.error) {
        setState((state) => ({
          ...state,
          error: response.error,
        }));
      } else {
        setState({ ...INITIAL_STATE });
        handleNotifyParent();
      }
    } catch (error) {
      setState((state) => ({
        ...state,
        error: error as string,
      }));
    } finally {
      setState((state) => ({ ...state, loading: false }));
    }
  }, [INITIAL_STATE, state.item, handleNotifyParent]);
  //#endregion

  //#region JSX
  return (
    <>
      <Button
        onClick={() => setState((state) => ({ ...state, open: true }))}
        title={noChangesAllowed ? `${t(`buttons.view`)}` : `${t(`buttons.edit`)}`}
        style={{ color: theme.palette.text.primary }}
      >
        {noChangesAllowed ? `${t(`buttons.view`)}` : `${t(`buttons.edit`)}`}
      </Button>

      <Dialog
        open={state.open}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          handleClose={() =>
            setState((state) => ({
              ...state,
              ...(noChangesAllowed ? { open: false } : { CCDOpen: true }),
            }))
          }
          title={`${t(`${tRP}.title`)}`}
        />
        <DialogContent>
          <LoadingOverlay
            loading={state.loading}
            error={state.error}
          >
            <Grid>
              <Field>
                <Label>{`${t(`common_words.document`)}`} :</Label>
                {state.item.document && (
                  <FileIconButton
                    onClick={() =>
                      setState((state) => ({ ...state, fileToView: state.item.document?.files[0] ?? null }))
                    }
                  >
                    <FileIcon
                      extension={getFileExtension(state.item.document?.files[0]?.name)}
                      {...customFileIconStyles[
                        getFileExtension(state.item.document?.files[0]?.name) as DefaultExtensionType
                      ]}
                    />
                  </FileIconButton>
                )}
              </Field>
              <Field>
                <Label>{`${t(`${tRPFields}.to_be`)}`} : </Label>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={state.item.toBeStamped}
                      onChange={({ currentTarget }) => handleChange("toBeStamped", currentTarget.checked)}
                      disabled={noChangesAllowed}
                    />
                  }
                  label={`${t(`${tRPFields}.stamped`)}`}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={state.item.toBeSigned}
                      onChange={({ currentTarget }) => handleChange("toBeSigned", currentTarget.checked)}
                      disabled={noChangesAllowed}
                    />
                  }
                  label={`${t(`${tRPFields}.signed`)}`}
                />
              </Field>
            </Grid>
            <Container>
              <Block>
                {noChangesAllowed ? (
                  <Field>
                    <Label>{`${t(`${tRPFields}.post_via`)}`} : </Label>
                    <Typography>{state.item.postVia?.name}</Typography>
                  </Field>
                ) : (
                  <AutocompleteDropdown
                    serviceApi={new OutgoingMailPostingOptionApiService()}
                    field={"postVia"}
                    handleChange={handleChange}
                    labelKey={`${t(`${tRPFields}.post_via`)}`}
                    disabled={noChangesAllowed}
                  />
                )}

                {noChangesAllowed ? (
                  <Field>
                    <Label>{`${t(`${tRPFields}.address_nickname`)}`} : </Label>
                    <Typography>{state.item.addressedTo?.nickname}</Typography>
                  </Field>
                ) : (
                  <CustomFetchingDropdown
                    apiService={new MailInboxAddresseesApiService()}
                    fieldProp="nickname"
                    selectedValue={state.item.addressedTo as MailInboxAddresseeDTO}
                    label={`${t(`${tRPFields}.address_nickname`)}`}
                    handleChangeParent={(_item: MailInboxAddresseeDTO | null) =>
                      setState((state) => ({ ...state, newAddressee: _item }))
                    }
                    propsFilter={
                      {
                        mailInboxIds: state.item.inbox ? [state.item.inbox.id] : [],
                        isActive: true,
                      } as MailInboxAddresseeFilter
                    }
                    childFields={[
                      {
                        field: "companyName",
                      },
                      {
                        field: "contactPersonName",
                      },
                      {
                        field: "addressLineOne",
                      },
                      {
                        field: "location",
                      },
                      {
                        field: "zip",
                      },
                    ]}
                    {...(state.item.addressedToOcr && {
                      taggedMenuOption: {
                        id: state.item.addressedToOcr.id,
                        tag: <SuggestedByAiHint />,
                        displayTagWhenSelected: item.addressedTo ? false : true,
                      },
                    })}
                  />
                )}

                {state.item.inbox && !noChangesAllowed && (
                  <AddEditMailInboxAddressee
                    effect="add"
                    handleNotifyParent={(item) => handleChange("addressedTo", item)}
                    buttonText={`${t(`${tRP}.addressed_to_not_in_the_list`)}`}
                    mailInbox={state.item.inbox} //TODO MDT CLEANUP
                    buttonVariant="text"
                    customButtonStyle={{
                      color: theme.palette.primary.main,
                      justifyContent: "left",
                      fontSize: "12px",
                      height: "12px",
                      textTransform: "none",
                    }}
                  />
                )}

                {noChangesAllowed ? (
                  <Field>
                    <Label>{`${t(`${tRPFields}.contact_person_name`)}`} : </Label>
                    <Typography>{state.item.contactPersonName}</Typography>
                  </Field>
                ) : (
                  <TextField
                    value={state.item.contactPersonName}
                    onChange={({ currentTarget }) => handleChange("contactPersonName", currentTarget.value)}
                    label={`${t(`${tRPFields}.contact_person_name`)}`}
                    disabled={noChangesAllowed}
                    {...(state.item.contactPersonNameOcr && {
                      InputProps: {
                        endAdornment: (
                          <InputAdornment position="end">
                            {state.item.contactPersonName.trim() === state.item.contactPersonNameOcr.trim() ? (
                              item.contactPersonName ? (
                                <></>
                              ) : (
                                <Tooltip title="Suggested by AI">
                                  <SuggestedByAiHint />
                                </Tooltip>
                              )
                            ) : (
                              <SimplePopover
                                buttonType="text"
                                buttonContent={
                                  <Typography style={{ textDecoration: "none", fontSize: "10px" }}>
                                    {`${t(`${tRP}.check_ai_suggestion`)}`}
                                  </Typography>
                                }
                                anchorOrigin={{
                                  vertical: "bottom",
                                  horizontal: "left",
                                }}
                                transformOrigin={{
                                  vertical: "top",
                                  horizontal: "left",
                                }}
                                children={
                                  <Button
                                    style={{ textDecoration: "none" }}
                                    onClick={() => handleChange("contactPersonName", state.item.contactPersonNameOcr)}
                                  >
                                    {state.item.contactPersonNameOcr}
                                  </Button>
                                }
                              />
                            )}
                          </InputAdornment>
                        ),
                      },
                    })}
                  />
                )}
                {noChangesAllowed ? (
                  <Field>
                    <Label>{`${t(`${tRPFields}.phone_number`)}`} : </Label>
                    <Typography>{state.item.phoneNumber}</Typography>
                  </Field>
                ) : (
                  <TextField
                    value={state.item.phoneNumber}
                    onChange={({ currentTarget }) => handleChange("phoneNumber", currentTarget.value.trim())}
                    label={`${t(`${tRPFields}.phone_number`)}`}
                    error={!!state.item.phoneNumber && !isValidString(PHONE_NUMBER_PATTERN, state.item.phoneNumber)}
                    helperText={
                      !!state.item.phoneNumber &&
                      !isValidString(PHONE_NUMBER_PATTERN, state.item.phoneNumber) &&
                      `${t(`${tRP}.invalid_phone_number`)}`
                    }
                    disabled={noChangesAllowed}
                    {...(state.item.phoneNumberOcr && {
                      InputProps: {
                        endAdornment: (
                          <InputAdornment position="end">
                            {state.item.phoneNumber?.trim() === state.item.phoneNumberOcr?.trim() ? (
                              item.phoneNumber ? (
                                <></>
                              ) : (
                                <SuggestedByAiHint />
                              )
                            ) : (
                              <SimplePopover
                                buttonType="text"
                                buttonContent={
                                  <Typography style={{ textDecoration: "none", fontSize: "10px" }}>
                                    {`${t(`${tRP}.check_ai_suggestion`)}`}
                                  </Typography>
                                }
                                anchorOrigin={{
                                  vertical: "bottom",
                                  horizontal: "left",
                                }}
                                transformOrigin={{
                                  vertical: "top",
                                  horizontal: "left",
                                }}
                                children={
                                  <Button
                                    style={{ textDecoration: "none" }}
                                    onClick={() => handleChange("phoneNumber", state.item.phoneNumberOcr)}
                                  >
                                    {state.item.phoneNumberOcr}
                                  </Button>
                                }
                              />
                            )}
                          </InputAdornment>
                        ),
                      },
                    })}
                  />
                )}

                {noChangesAllowed ? (
                  <Field>
                    <Label>{`${t(`${tRPFields}.email`)}`} : </Label>
                    <Typography>{state.item.email}</Typography>
                  </Field>
                ) : (
                  <TextField
                    value={state.item.email}
                    onChange={({ currentTarget }) => handleChange("email", currentTarget.value)}
                    label={`${t(`${tRPFields}.email`)}`}
                    disabled={noChangesAllowed}
                    error={!!state.item.email && !isValidString(EMAIL_PATTERN, state.item.email)}
                    helperText={
                      !!state.item.email &&
                      !isValidString(EMAIL_PATTERN, state.item.email) &&
                      `${t(`${tRP}.invalid_mail`)}`
                    }
                    {...(state.item.emailOcr && {
                      InputProps: {
                        endAdornment: (
                          <InputAdornment position="end">
                            {state.item.email?.trim() === state.item.emailOcr?.trim() ? (
                              item.email ? (
                                <></>
                              ) : (
                                <SuggestedByAiHint />
                              )
                            ) : (
                              <SimplePopover
                                buttonType="text"
                                buttonContent={
                                  <Typography style={{ textDecoration: "none", fontSize: "10px" }}>
                                    {`${t(`${tRP}.check_ai_suggestion`)}`}
                                  </Typography>
                                }
                                anchorOrigin={{
                                  vertical: "bottom",
                                  horizontal: "left",
                                }}
                                transformOrigin={{
                                  vertical: "top",
                                  horizontal: "left",
                                }}
                                children={
                                  <Button
                                    style={{ textDecoration: "none" }}
                                    onClick={() => handleChange("email", state.item.emailOcr)}
                                  >
                                    {state.item.emailOcr}
                                  </Button>
                                }
                              />
                            )}
                          </InputAdornment>
                        ),
                      },
                    })}
                  />
                )}
                {noChangesAllowed ? (
                  <Field>
                    <Label>{`${t(`${tRPFields}.company_name`)}`} : </Label>
                    <Typography>{state.item.companyName}</Typography>
                  </Field>
                ) : (
                  <TextField
                    value={state.item.companyName}
                    onChange={({ currentTarget }) => handleChange("companyName", currentTarget.value)}
                    label={`${t(`${tRPFields}.company_name`)}`}
                    disabled={noChangesAllowed}
                    {...(state.item.companyNameOcr && {
                      InputProps: {
                        endAdornment: (
                          <InputAdornment position="end">
                            {state.item.companyName?.trim() === state.item.companyNameOcr?.trim() ? (
                              item.companyName ? (
                                <></>
                              ) : (
                                <SuggestedByAiHint />
                              )
                            ) : (
                              <SimplePopover
                                buttonType="text"
                                buttonContent={
                                  <Typography style={{ textDecoration: "none", fontSize: "10px" }}>
                                    {`${t(`${tRP}.check_ai_suggestion`)}`}
                                  </Typography>
                                }
                                anchorOrigin={{
                                  vertical: "bottom",
                                  horizontal: "left",
                                }}
                                transformOrigin={{
                                  vertical: "top",
                                  horizontal: "left",
                                }}
                                children={
                                  <Button
                                    style={{ textDecoration: "none" }}
                                    onClick={() => handleChange("companyName", state.item.companyNameOcr)}
                                  >
                                    {state.item.companyNameOcr}
                                  </Button>
                                }
                              />
                            )}
                          </InputAdornment>
                        ),
                      },
                    })}
                  />
                )}
              </Block>

              <Block>
                {noChangesAllowed ? (
                  <Field>
                    <Label>{`${t(`${tRPFields}.address_line_one`)}`} : </Label>
                    <Typography>{state.item.addressLineOne}</Typography>
                  </Field>
                ) : (
                  <TextField
                    value={state.item.addressLineOne}
                    onChange={({ currentTarget }) => handleChange("addressLineOne", currentTarget.value)}
                    label={`${t(`${tRPFields}.address_line_one`)}`}
                    disabled={noChangesAllowed}
                    {...(state.item.addressLineOneOcr && {
                      InputProps: {
                        endAdornment: (
                          <InputAdornment position="end">
                            {state.item.addressLineOne?.trim() === state.item.addressLineOneOcr?.trim() ? (
                              item.addressLineOne ? (
                                <></>
                              ) : (
                                <SuggestedByAiHint />
                              )
                            ) : (
                              <SimplePopover
                                buttonType="text"
                                buttonContent={
                                  <Typography style={{ textDecoration: "none", fontSize: "10px" }}>
                                    {`${t(`${tRP}.check_ai_suggestion`)}`}
                                  </Typography>
                                }
                                anchorOrigin={{
                                  vertical: "bottom",
                                  horizontal: "left",
                                }}
                                transformOrigin={{
                                  vertical: "top",
                                  horizontal: "left",
                                }}
                                children={
                                  <Button
                                    style={{ textDecoration: "none" }}
                                    onClick={() => handleChange("addressLineOne", state.item.addressLineOneOcr)}
                                  >
                                    {state.item.addressLineOneOcr}
                                  </Button>
                                }
                              />
                            )}
                          </InputAdornment>
                        ),
                      },
                    })}
                  />
                )}

                {noChangesAllowed ? (
                  <Field>
                    <Label>{`${t(`${tRPFields}.address_line_two`)}`} :</Label>
                    <Typography>{state.item.addressLineTwo}</Typography>
                  </Field>
                ) : (
                  <TextField
                    value={state.item.addressLineTwo}
                    onChange={({ currentTarget }) => handleChange("addressLineTwo", currentTarget.value)}
                    label={`${t(`${tRPFields}.address_line_two`)}`}
                    disabled={noChangesAllowed}
                    {...(state.item.addressLineTwoOcr && {
                      InputProps: {
                        endAdornment: (
                          <InputAdornment position="end">
                            {state.item.addressLineTwo.trim() === state.item.addressLineTwoOcr.trim() ? (
                              item.addressLineTwo ? (
                                <></>
                              ) : (
                                <SuggestedByAiHint />
                              )
                            ) : (
                              <SimplePopover
                                buttonType="text"
                                buttonContent={
                                  <Typography style={{ textDecoration: "none", fontSize: "10px" }}>
                                    {`${t(`${tRP}.check_ai_suggestion`)}`}
                                  </Typography>
                                }
                                anchorOrigin={{
                                  vertical: "bottom",
                                  horizontal: "left",
                                }}
                                transformOrigin={{
                                  vertical: "top",
                                  horizontal: "left",
                                }}
                                children={
                                  <Button
                                    style={{ textDecoration: "none" }}
                                    onClick={() => handleChange("addressLineTwo", state.item.addressLineTwoOcr)}
                                  >
                                    {state.item.addressLineTwoOcr}
                                  </Button>
                                }
                              />
                            )}
                          </InputAdornment>
                        ),
                      },
                    })}
                  />
                )}
                {noChangesAllowed ? (
                  <Field>
                    <Label>{`${t(`${tRPFields}.zip`)}`} :</Label>
                    <Typography>{state.item.zip}</Typography>
                  </Field>
                ) : (
                  <TextField
                    value={state.item.zip}
                    onChange={({ currentTarget }) => handleChange("zip", currentTarget.value)}
                    label={`${t(`${tRPFields}.zip`)}`}
                    disabled={noChangesAllowed}
                    {...(state.item.zip && {
                      InputProps: {
                        endAdornment: (
                          <InputAdornment position="end">
                            {state.item.zip?.trim() === state.item.zipOcr?.trim() ? (
                              item.zip ? (
                                <></>
                              ) : (
                                <SuggestedByAiHint />
                              )
                            ) : state.item.zipOcr ? (
                              <SimplePopover
                                buttonType="text"
                                buttonContent={
                                  <Typography style={{ textDecoration: "none", fontSize: "10px" }}>
                                    {`${t(`${tRP}.check_ai_suggestion`)}`}
                                  </Typography>
                                }
                                anchorOrigin={{
                                  vertical: "bottom",
                                  horizontal: "left",
                                }}
                                transformOrigin={{
                                  vertical: "top",
                                  horizontal: "left",
                                }}
                                children={
                                  <Button
                                    style={{ textDecoration: "none" }}
                                    onClick={() => handleChange("zip", state.item.zipOcr)}
                                  >
                                    {state.item.zipOcr}
                                  </Button>
                                }
                              />
                            ) : (
                              ""
                            )}
                          </InputAdornment>
                        ),
                      },
                    })}
                  />
                )}
                {noChangesAllowed ? (
                  <Field>
                    <Label>{`${t(`${tRPFields}.location`)}`} :</Label>
                    <Typography>{state.item.location}</Typography>
                  </Field>
                ) : (
                  <TextField
                    value={state.item.location}
                    onChange={({ currentTarget }) => handleChange("location", currentTarget.value)}
                    label={`${t(`${tRPFields}.location`)}`}
                    disabled={noChangesAllowed}
                    {...(state.item.location && {
                      InputProps: {
                        endAdornment: (
                          <InputAdornment position="end">
                            {state.item.location?.trim() === state.item.locationOcr?.trim() ? (
                              item.location ? (
                                <></>
                              ) : (
                                <SuggestedByAiHint />
                              )
                            ) : state.item.locationOcr ? (
                              <SimplePopover
                                buttonType="text"
                                buttonContent={
                                  <Typography style={{ textDecoration: "none", fontSize: "10px" }}>
                                    {`${t(`${tRP}.check_ai_suggestion`)}`}
                                  </Typography>
                                }
                                anchorOrigin={{
                                  vertical: "bottom",
                                  horizontal: "left",
                                }}
                                transformOrigin={{
                                  vertical: "top",
                                  horizontal: "left",
                                }}
                                children={
                                  <Button
                                    style={{ textDecoration: "none" }}
                                    onClick={() => handleChange("location", state.item.locationOcr)}
                                  >
                                    {state.item.locationOcr}
                                  </Button>
                                }
                              />
                            ) : (
                              ""
                            )}
                          </InputAdornment>
                        ),
                      },
                    })}
                  />
                )}
                {noChangesAllowed ? (
                  <Field>
                    <Label>{`${t(`${tRPFields}.country`)}`} :</Label>
                    <Typography>{state.item.country?.name}</Typography>
                  </Field>
                ) : (
                  <AutocompleteDropdown
                    selectedValue={state.item.country}
                    serviceApi={new CountriesApiService()}
                    field={"country"}
                    handleChange={handleChange}
                    labelKey={`${t(`${tRPFields}.country`)}`}
                    disabled={noChangesAllowed}
                    {...(state.item.countryOcr && {
                      taggedMenuOption: {
                        id: state.item.countryOcr.id,
                        tag: <SuggestedByAiHint />,
                        displayTagWhenSelected: item.country ? false : true,
                      },
                    })}
                  />
                )}
                {noChangesAllowed ? (
                  <Field>
                    <Label>{`${t(`${tRPFields}.comment`)}`} :</Label>
                    <Typography>{state.item.comment}</Typography>
                  </Field>
                ) : (
                  <TextField
                    value={state.item.comment}
                    onChange={({ currentTarget }) => handleChange("comment", currentTarget.value)}
                    label={`${t(`${tRPFields}.comment`)}`}
                    disabled={noChangesAllowed}
                  />
                )}
              </Block>
            </Container>
          </LoadingOverlay>
        </DialogContent>
        <DialogActions
          handleCancel={() =>
            setState((state) => ({
              ...state,
              ...(noChangesAllowed ? { open: false } : { CCDOpen: true }),
            }))
          }
          {...(!noChangesAllowed && {
            handleSave: handleSave,
          })}
          children={<Alert severity="info">{`${t(`${tRP}.save_to_confirm_ai_suggestion`)}`}</Alert>}
        />
      </Dialog>
      <ConfirmationChangesDialog
        open={state.CCDOpen}
        handleCancel={() => setState((state) => ({ ...state, CCDOpen: false }))}
        handleDiscard={() => setState({ ...INITIAL_STATE })}
      />
      <CustomConfirmationChangesDialog
        title={`${t(`${tRP}.override_addressee_data_title`)}`}
        open={state.CCCDOpen}
        loading={false}
        handleCancel={() => {
          setState((state) => ({ ...state, newAddressee: null, CCCDOpen: false }));
        }}
        actionsAndHandlers={[
          {
            action: `${t(`${tRP}.override_existing_data`)}`,
            handler: () => handleChangeAdressedTo(true),
            customButtonStyle: {
              backgroundColor: theme.palette.warning.main,
              color: theme.palette.primary.contrastText,
            },
          },
          {
            action: `${t(`${tRP}.do_not_override_existing_data`)}`,
            handler: () => handleChangeAdressedTo(false),
            customButtonStyle: {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
            },
          },
        ]}
        children={<Typography> {`${t(`${tRP}.override_addressee_data_message`)}`}</Typography>}
      />
      {state.fileToView && (
        <DocumentViewerContainer
          open={!!state.fileToView}
          fullWidth
        >
          <DialogTitle
            title=""
            handleClose={() => {
              setState((state) => ({ ...state, fileToView: null }));
            }}
          />
          <Grid style={{ width: "90vw" }}>
            <DocumentViewer
              fileDto={state.fileToView}
              isGenericDocument
            />
          </Grid>
        </DocumentViewerContainer>
      )}
    </>
  );
};

export default EditOutgoingMail;
